/**
 * 테이블 분석 — **SQL 한 건이 아니라 테이블이 단위다.**
 *
 * 통계·실행계획 술어를 모아 인덱스·파티션·논리모델 설계안을 낸다. 지금까지의 튜닝은
 * sql_id → 계획 → 개선안이었는데, 여기는 테이블에서 시작해 그 테이블을 건드린 SQL 전부를 모은다.
 *
 * ## 왜 단계가 나뉘어 있나
 *
 *   1 대상 → 2 수집(DB 를 읽는다) → 3 리포트(AI 를 부른다) → 4 사례 등록
 *
 * 2와 3은 **비용도 실패 방식도 다르다.** 수집은 권한·시간의 문제이고 리포트는 토큰을 쓴다.
 *
 * ## 한 창, 세 탭 (2026-10-06 사용자 요구)
 *
 *   [테이블 분석] 대상 → 수집(통계·실행계획 조건·**조건 조합**·기간별 행 수) → 기초 분석 결과(AI 없이 계산한 것) → 분석 리포트(진단, AI)
 *   [인덱스 설계] [파티션 설계] **테이블 분석의 결과를 쓴다** — 수집한 사실 + 그 테이블의 분석 리포트 진단 + 고른 설계 가이드
 *                → 설계안(AI) → 설계서·설계 이력(DesignDocPanel)
 *
 * 대상·수집은 세 탭이 **한 벌**을 함께 쓴다(설계마다 다시 수집하지 않는다). 리포트 단계는 탭마다 따로(ReportStage) —
 * 탭을 오가도 각 탭의 결과가 남게 숨기기만 하고 내리지 않는다. 메뉴의 세 항목은 이 창을 그 탭으로 연다.
 *
 * 설계 탭은 절을 사람이 고르지 않는다(서버가 정한다 — 인덱스 설계도 넓은 범위 조회면 파티션 절이 붙는다).
 *
 * ## 조사 원본은 사용자별로 저장한다 (2026-10-06)
 *
 * 수집은 저장하지 않는다 — 원본(통계 행·SQL 전문·실행계획 조건 원문)은 서버에 잠시 두고(collectId) 화면에는 요약만 온다.
 * 「저장」 하면 이미 저장된 테이블은 **교체/병합을 테이블별로** 고른다. 저장 뒤 화면은 저장된 원본에서 다시 만든 요약으로 바뀐다
 * (병합이면 앞서 쌓은 SQL 까지). 「저장된 분석」 에서 수집 없이 불러와 설계할 수 있다. 원본 보기는 주인만, AI 로는 요약만 간다.
 * 한 번 수집해 두고 절(인덱스/파티션/모델)을 바꿔 가며 여러 번 리포트할 수 있어야 한다.
 *
 * ## 리포트는 **테이블 단위**다 (사용자 결정 2026-09-11)
 *
 * 여러 테이블을 한 번에 보내 종합 리포트를 받으면, 제안이 어느 테이블의 것인지 흐려지고
 * 사례로 옮길 때 한 테이블의 결론만 떼어낼 수가 없다. 그래서 **테이블마다 한 번씩** 부르고
 * 결과를 탭으로 나눈다. 대신 호출이 대상 수만큼 늘어나므로 — 토큰을 쓰는 일이다 —
 * 버튼 옆에 AI 를 부른다는 것과 몇 번 부르는지를 적고, 끝나면 사용량을 남긴다.
 *
 * 순차로 돈다. 동시에 N개를 부르면 제공자 CLI/SDK 프로세스가 N개 뜬다. 한 건이 실패해도
 * **앞서 끝난 것은 남긴다** — 열 개 중 하나 때문에 전부 잃으면 다시 열 번을 부른다.
 *
 * ## 근거를 먼저 보여 준다
 *
 * 2단계 결과를 3단계 전에 그대로 보여 주는 이유: AI 가 무엇을 읽고 말하는지 사람이 먼저 알아야
 * 한다. 통계가 없거나 액세스 패턴이 안 잡힌 채로 나온 설계안은 근거가 없는데, 리포트만 보면
 * 그 사실이 보이지 않는다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FiX, FiSearch, FiCpu, FiLoader, FiAlertCircle, FiSave, FiDatabase, FiPlus, FiLayers, FiList, FiClock,
} from 'react-icons/fi';
import {
    analyzeTables, buildTableReport, tableCaseDraft, saveTuningCase, getCaseTopics,
    listAnalysisObjects, listDesignGuides,
    saveTableAnalysis, listSavedTables, loadSavedTables, deleteSavedTable, ruleDiagnose,
    listTableDiagnoses, getTableDiagnosis, latestTableDiagnoses,
} from '../../services/assistantClient';
import DesignDocPanel from './DesignDocPanel';
import TableRawPanel from './TableRawPanel';
import { useConfirm } from '../ConfirmDialog';
import DesignGuideModal from './DesignGuideModal';
import useModalWindow from '../common/useModalWindow';
import { storedTime } from '../../utils/storedTime';
import memoWindow from '../common/memoWindow';

/** 이 설계에 해당하는 가이드 종류 — 인덱스 설계도 넓은 범위 조회면 파티션 절이 붙으므로 파티션 가이드를 함께 고를 수 있다 */
const guideKindsFor = (design) => (design === 'partition' ? ['partition'] : ['index', 'partition']);
/** 매핑이 지금 대상에 맞는가 — 스키마 빈 매핑은 그 DB 전체(서버 designGuide/store.matchesTarget 과 같은 판정) */
const mappedTo = (g, connId, schema) => (g.mappings || [])
    .some(m => m.connectionId === connId && (!m.schema || m.schema === String(schema || '').toUpperCase()));

const SECTIONS = ['index', 'partition', 'model'];

/** 선택한 칩을 다 보여 주면 목록이 화면을 밀어낸다. 나머지는 개수로 말한다. */
const CHIP_MAX = 12;
/**
 * 내부 표기 대신 뜻으로 보이는 가공 — INTERNAL_FUNCTION 은 Oracle 의 암시적 형변환, ARITH 는 컬럼에 붙은 산술(`col + 0` 등).
 * 둘 다 그 컬럼의 인덱스를 못 탄다.
 */
const SPECIAL_FN = { INTERNAL_FUNCTION: 'assistant:tableAnalysis.implicitConv', ARITH: 'assistant:tableAnalysis.arithExpr' };
/** 근거 단계에 보이는 조건 조합 수 — 나머지는 수로 말한다(전부는 AI 사실 목록·설계서에 실린다) */
const COMBO_SHOWN = 8;

/**
 * **말할 것이 있는 리포트인가.** 모델이 "특별히 없다" 로 끝내면 탭을 만들어 봐야 빈 화면이고,
 * 사례로 등록할 것도 없다. 없는 것은 없다고 말하되(아래 `noFindings`) 탭은 만들지 않는다.
 */
function hasFindings(data) {
    if (!data || !data.report) return false;
    return (data.sections || []).some((sc) => {
        const sec = data.report[sc];
        return !!sec && (((sec.findings || []).length > 0) || ((sec.proposals || []).length > 0));
    });
}

/** 진단의 지적 수 — 목록·설계 근거 줄에 적는다 */
function findingCount(data) {
    if (!data || !data.report) return 0;
    return (data.sections || []).reduce((n, sc) => n + (((data.report[sc] || {}).findings || []).length), 0);
}
/** 진단 판의 이름 — AI 진단 / 규칙 진단 / 규칙 진단(설계 직전 자동 생성) */
const diagKind = (d, t) => t(d && d.method === 'rule'
    ? (d.auto ? 'assistant:tableAnalysis.diagKind.ruleAuto' : 'assistant:tableAnalysis.diagKind.rule')
    : 'assistant:tableAnalysis.diagKind.ai');
/** 저장 시각을 짧게(연도는 뺀다 — 목록이 좁다) */
const shortAt = (at) => storedTime(at, 'short');

/** 1,234 / 12.3k — 사용량은 **언제나 보인다**(docs/subscription-tiers.md 결정 ⑧). */
const compact = (n) => {
    const v = Number(n) || 0;
    if (v < 1000) return String(v);
    if (v < 1000000) return `${(v / 1000).toFixed(v < 10000 ? 1 : 0)}k`;
    return `${(v / 1000000).toFixed(1)}M`;
};
/** 아주 작은 금액이 0 으로 보이면 안 된다. 없는 값을 단가표로 추정하지는 않는다. */
const money = (usd) => (usd < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(2)}`);

/** 실패는 **닫기 전까지 남는다.** 토스트로 흘리면 무엇이 왜 안 됐는지 사라진다. */
function Problem({ error, onClose, t }) {
    if (!error) return null;
    // 사례 등록 양식과 **같은 자리·같은 모양**을 쓴다 — 실패가 화면마다 다르게 보이면 안 된다
    return (
        <div className="tcf-leak">
            <FiAlertCircle size={12} />
            <div className="tcf-leakBody">
                <div>{error.message}</div>
                {error.raw ? <pre className="tcf-leakRaw">{error.raw}</pre> : null}
            </div>
            <button type="button" className="tcf-iconBtn" onClick={onClose} title={t('common:ui.close')}>
                <FiX size={11} />
            </button>
        </div>
    );
}
Problem.propTypes = {
    error: PropTypes.shape({ message: PropTypes.string, raw: PropTypes.string }),
    onClose: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};
Problem.defaultProps = { error: null };

/** 한 테이블의 리포트 본문. 탭이 가리키는 것 하나만 그린다. */
function ReportBody({ data, t }) {
    const r = data.report;
    return (
        <div className="ta-tabBody">
            {/* 어느 판인지 — 같은 화면에서 AI 진단과 규칙 진단을 바꿔 보므로 늘 적는다 */}
            {data.diagnosis ? (
                <div className="ta-diagHead">
                    <span className={data.method === 'rule' ? 'ta-dbonly' : 'ta-ai'}>
                        {data.method === 'rule' ? <FiList size={10} /> : <FiCpu size={10} />}
                        {diagKind(data, t)}
                    </span>
                    {data.createdAt ? <span className="ta-meta">{storedTime(data.createdAt)}</span> : null}
                    {data.basis === 'collected' ? <span className="ta-meta">{t('assistant:tableAnalysis.diagCollected')}</span> : null}
                </div>
            ) : null}
            {/* 진단한 뒤에 같은 테이블을 다시 저장(병합·교체)했으면 그 진단은 옛 사실 위에 서 있다 */}
            {data.stale ? <div className="ta-gap">{t('assistant:tableAnalysis.diagStale')}</div> : null}
            <div className="ta-summary">{r.summary}</div>
            {/* 설계의 근거 — 참조한 진단과 설계 가이드(2026-10-07: 설계는 둘을 참조한다). 없으면 없다고 말한다 */}
            {data.design && data.prior ? (
                <div className="tcf-hint">
                    {t('assistant:design.priorApplied', { kind: diagKind(data.prior, t), at: storedTime(data.prior.createdAt) || '-', count: data.prior.findings || 0 })}
                </div>
            ) : null}
            {data.guide && (data.guide.applied || []).length ? (
                <div className="tcf-hint">{t('assistant:design.guideApplied', { titles: data.guide.applied.join(', ') })}</div>
            ) : null}
            {data.design && !(data.guide && (data.guide.applied || []).length) ? (
                <div className="tcf-hint">{t('assistant:design.guideNone')}</div>
            ) : null}
            {data.guide && (data.guide.skipped || []).length ? (
                <div className="tcf-hint">{t('assistant:design.guideSkipped', { titles: data.guide.skipped.join(', ') })}</div>
            ) : null}
            {data.sections.map(sc => (
                <div key={sc}>
                    <div className="tcf-checkHead">
                        {t(data.diagnosis ? `assistant:tableAnalysis.diagSection.${sc}` : `assistant:tableAnalysis.section.${sc}`)}
                    </div>
                    {/* 빈 관점은 「살펴봤고 지적이 없다」 고 말한다 — 빈 제목만 두면 못 본 것인지 알 수 없다 */}
                    {!(r[sc].findings || []).length && !(r[sc].proposals || []).length ? (
                        <div className="tcf-hint">{t('assistant:tableAnalysis.sectionClean')}</div>
                    ) : null}
                    {(r[sc].findings || []).map(f => (
                        <div className="ta-finding" key={f.title}>
                            <span className={`ta-sev ta-sev-${f.severity}`}>{f.severity}</span>
                            <span>
                                {f.title}
                                {f.evidence ? <em className="ta-evidence">{f.evidence}</em> : null}
                            </span>
                        </div>
                    ))}
                    {(r[sc].proposals || []).map(pr => (
                        <div className="ta-proposal" key={`${pr.action}${pr.table}${pr.columns.join()}`}>
                            <b>{pr.action}</b> {pr.table}
                            {pr.columns.length ? ` [${pr.columns.join(', ')}]` : ''}
                            {pr.index ? ` (${pr.index})` : ''}
                            {pr.partitionType ? ` · ${pr.partitionType}` : ''}
                            {pr.interval ? ` · ${pr.interval}` : ''}
                            {pr.reason ? <div className="ta-reason">{pr.reason}</div> : null}
                            {pr.range ? <div className="ta-reason">{t('assistant:design.range')}: {pr.range}</div> : null}
                            {pr.indexNote ? <div className="ta-reason">{t('assistant:design.indexNote')}: {pr.indexNote}</div> : null}
                            {pr.ddl ? <pre className="ta-ddl">{pr.ddl}</pre> : null}
                        </div>
                    ))}
                </div>
            ))}
            {r.caution ? <div className="tcf-checkCaution"><pre>{r.caution}</pre></div> : null}
            {/* **버린 제안을 숨기지 않는다** — 지어낸 이름이 있었다는 것은 그 리포트를
                얼마나 믿을지 판단하는 근거다 */}
            {(r.rejected || []).length ? (
                <div className="gm-dropped">
                    <FiAlertCircle size={12} />
                    {t('assistant:tableAnalysis.rejected', { count: r.rejected.length })}
                    <ul className="ta-cols">
                        {r.rejected.map(x => <li key={x.reason}>{x.reason}</li>)}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}
ReportBody.propTypes = {
    data: PropTypes.shape({ report: PropTypes.object, sections: PropTypes.array }).isRequired,
    t: PropTypes.func.isRequired,
};

/** 탭(리본) — 한 창 안의 세 갈래(2026-10-06 사용자 요구: 테이블 분석·인덱스 설계·파티션 설계를 한 팝업으로) */
const TAB_LABEL = { analysis: 'menu:tools.tableAnalysis', index: 'menu:tools.indexDesign', partition: 'menu:tools.partitionDesign' };

/**
 * 리포트 단계(+ 사례 등록) — 탭마다 **하나씩, 따로** 둔다. 탭을 오가도 각 탭의 결과가 남아야 하고(그래서 숨길 뿐 내리지 않는다),
 * 분석 리포트와 설계안이 한 상태에 섞이면 어느 것이 무엇의 결과인지 흐려진다.
 *
 * @param design null = 테이블 분석(진단 리포트, 절을 고른다) · 'index'|'partition' = 설계(절은 서버가 정한다)
 * @param prior  테이블 키 → 테이블 분석 탭의 리포트. 설계는 이 진단을 함께 받는다(「테이블 분석의 결과를 사용」)
 */
function ReportStage({
    design, analysis, connectionId, schema, provider, prior, seed, onReports, onOpenGuides, guideReloadKey, active,
}) {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const rows = analysis.tables;
    // 분석 탭의 진단은 기본으로 인덱스·파티션 관점(설계 탭이 이 진단을 받아 쓴다)
    const [sections, setSections] = useState(['index', 'partition']);
    const [reporting, setReporting] = useState(false);
    /** 테이블 키 → `{ report, sections, usage }`. **테이블마다 한 번씩** 부른 결과다. */
    /**
     * 진단은 두 판(2026-10-06) — AI 진단 · 규칙 진단(AI 없음). 판마다 결과를 따로 두어 바꿔 보며 견줄 수 있다.
     * 설계는 판이 하나(AI)뿐이다.
     */
    // 저장된 분석을 불러오면 그때의 진단(seed — 지금 데이터 위의 것만)이 함께 뜬다. 규칙 진단만 있으면 그 판으로 시작한다
    const [method, setMethod] = useState(() => (
        seed && !Object.keys(seed.ai || {}).length && Object.keys(seed.rule || {}).length ? 'rule' : 'ai'));
    const [byMethod, setByMethod] = useState(() => ({ ai: { ...((seed && seed.ai) || {}) }, rule: { ...((seed && seed.rule) || {}) } }));
    const m = design ? 'ai' : method;
    const reports = byMethod[m];
    const setReports = useCallback((upd) => setByMethod(prev => ({
        ...prev, [m]: typeof upd === 'function' ? upd(prev[m]) : upd,
    })), [m]);
    /** 테이블 키 → 실패 사유. 실패한 테이블을 목록에서 지우면 왜 없는지 아무도 모른다. */
    const [reportFails, setReportFails] = useState({});
    const [reportAt, setReportAt] = useState(null);      // { done, total, table } — 도는 중
    const [reportTab, setReportTab] = useState('');
    const [reportError, setReportError] = useState(null);

    // 설계 가이드 — 목록 · 고른 것(id) · 사용자가 손댔는지(손대기 전에는 매핑으로 기본 선택을 다시 계산한다)
    const [guideList, setGuideList] = useState([]);
    const [guideSel, setGuideSel] = useState([]);
    const [guideTouched, setGuideTouched] = useState(false);

    const [registering, setRegistering] = useState(null);  // 등록 중인 사례 초안
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [topicCatalog, setTopicCatalog] = useState([]);

    // 분석 리포트는 설계 탭이 쓴다 — 바뀔 때마다 위로 알린다
    // 설계 탭에는 테이블마다 AI 진단이 있으면 그것, 없으면 규칙 진단을 넘긴다
    useEffect(() => { if (onReports) onReports({ ...byMethod.rule, ...byMethod.ai }); }, [byMethod, onReports]);

    useEffect(() => {
        if (!design || !active) return;
        listDesignGuides()
            // 범용 인덱스·파티션 가이드도 고를 수 있다(읽기 전용, 고를 때만 실린다 — 튜닝 범용 가이드는 설계 종류가 아니라 걸러진다)
            .then(r => setGuideList([...(r.guides || []), ...(r.builtins || []).filter(g => g.kind !== 'tuning')]))
            .catch(() => setGuideList([]));
    }, [design, active, guideReloadKey]);
    const guideChoices = useMemo(
        // 배포된 가이드만 — 작성중 가이드는 설계에 실리지 않는다(서버 designGuide/store.pick 도 거른다)
        () => guideList.filter(g => g.active && g.status !== 'draft' && guideKindsFor(design).includes(g.kind)), [guideList, design],
    );
    /** 배포하지 않아 고를 수 없는 가이드 수 — 「등록된 가이드가 없다」 와 「있는데 배포 전이다」 를 가른다(같은 말로 하면 등록해 둔 사람이 헷갈린다) */
    const draftGuideCount = useMemo(
        () => guideList.filter(g => g.active && g.status === 'draft' && guideKindsFor(design).includes(g.kind)).length, [guideList, design],
    );
    // 기본 선택 = 지금 연결·스키마에 매핑된 가이드. 사용자가 손댄 뒤에는 건드리지 않는다
    useEffect(() => {
        if (guideTouched) return;
        setGuideSel(guideChoices.filter(g => mappedTo(g, connectionId, schema)).map(g => g.id));
    }, [guideChoices, connectionId, schema, guideTouched]);
    const toggleGuide = useCallback((id) => {
        setGuideTouched(true);
        setGuideSel(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    }, []);

    /**
     * 설계가 근거로 쓸 진단 — 테이블 액세스 분석 탭에 뜬 진단(방금 만든 것 · 불러온 저장 진단, AI 가 있으면 AI).
     * 화면은 **진단 이력의 id 만** 보낸다 — 본문을 걸러 프롬프트에 싣는 것은 서버(resolvePrior → priorBlock) 한 곳이다.
     * 없으면 서버가 규칙 진단을 만들어 쓴다(2026-10-07: 설계는 진단을 참조해야 한다) — 그 사실을 미리 말한다.
     */
    const priorFor = useCallback((key) => {
        const p = prior && prior[key];
        if (!p || !p.report || !p.diagId) return null;
        return { id: p.diagId, method: p.method === 'rule' ? 'rule' : 'ai', createdAt: p.createdAt || '', findings: findingCount(p) };
    }, [prior]);

    /**
     * **테이블마다 한 번씩** 부른다(사용자 결정 2026-09-11).
     *
     * 한 번에 몰아 보내면 제안이 어느 테이블의 것인지 흐려지고, 사례로 옮길 때 한 테이블의
     * 결론만 떼어낼 수가 없다. 대신 호출이 대상 수만큼 늘어난다 — 그래서 **순차로** 돌고
     * (동시에 부르면 제공자 프로세스가 N개 뜬다), 한 건이 실패해도 **앞서 끝난 것은 남긴다.**
     */
    const makeReport = useCallback(async () => {
        // 설계는 가이드를 참조해야 한다(2026-10-07) — 하나도 안 골랐으면 그대로 갈지 묻는다
        const pickedGuides = design ? guideChoices.filter(g => guideSel.includes(g.id)).map(g => g.id) : [];
        if (design && !pickedGuides.length) {
            const ok = await confirm({
                title: t('assistant:design.noGuideTitle'),
                message: guideChoices.length
                    ? t('assistant:design.noGuideSelected', { count: guideChoices.length })
                    : (draftGuideCount ? t('assistant:design.noGuidePublished', { count: draftGuideCount }) : t('assistant:design.noGuideRegistered')),
                confirmText: t('assistant:design.noGuideGo'),
            });
            if (!ok) return;
        }
        setReporting(true);
        setReportError(null);
        setReports({});
        setReportFails({});
        let firstWithContent = '';
        for (let i = 0; i < rows.length; i += 1) {
            const x = rows[i];
            setReportAt({ done: i, total: rows.length, table: x.key });
            try {
                // eslint-disable-next-line no-await-in-loop -- 순차가 의도다(위 주석)
                // 연결을 함께 보낸다 — 진단은 진단 이력에, 설계는 설계 이력에 남는다
                const r = m === 'rule' ? await ruleDiagnose({ tables: [x], sections, connectionId }) : await buildTableReport({
                    tables: [x], sections, provider, note: analysis.note, connectionId,
                    // 설계 — 절은 서버가 정하고, 설계서 초안이 이력에 남는다.
                    // 고른 가이드는 **목록 순서대로**(순서가 곧 우선순위 — 분량이 넘치면 뒤가 밀린다)
                    ...(design ? {
                        design, guideIds: pickedGuides,
                        priorDiagId: (priorFor(x.key) || {}).id || undefined,
                    } : {}),
                });
                setReports(prev => ({ ...prev, [x.key]: r }));
                if (!firstWithContent && (design || hasFindings(r))) firstWithContent = x.key;
            } catch (err) {
                const b = err.body || {};
                setReportFails(prev => ({ ...prev, [x.key]: { message: err.message, raw: b.raw } }));
            }
        }
        setReportAt(null);
        setReporting(false);
        if (firstWithContent) setReportTab(firstWithContent);
    }, [rows, analysis.note, sections, provider, design, connectionId, guideChoices, guideSel, priorFor, m, setReports, confirm, t, draftGuideCount]);

    /** 분석 결과 → 사례 초안. 서버가 결과·방안·근거를 각각 다른 칸에 담아 준다. */
    const startRegister = useCallback(async (table) => {
        setSaveError(null);
        try {
            const mine = reports[table.key];
            const [r, topics] = await Promise.all([
                tableCaseDraft({ table, report: mine ? mine.report : null }),
                topicCatalog.length ? Promise.resolve({ topics: topicCatalog }) : getCaseTopics(),
            ]);
            if (!topicCatalog.length) setTopicCatalog(topics.topics || []);
            setRegistering({ ...r.seed, visibility: 'internal' });
        } catch (err) {
            setSaveError({ message: err.message });
        }
    }, [reports, topicCatalog]);

    const save = useCallback(async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const r = await saveTuningCase({ ...registering, status: 'final' });
            toast.success(t('assistant:tableAnalysis.saved', { title: r.case.title }));
            setRegistering(null);
        } catch (err) {
            const b = err.body || {};
            setSaveError({ message: err.message, raw: b.missingAxes ? b.missingAxes.join(', ') : undefined });
        } finally { setSaving(false); }
    }, [registering, t]);

    /**
     * 탭은 **말할 것이 있는 테이블**만. 나머지는 아래에서 이유와 함께 말한다.
     * 설계는 리포트가 나온 테이블 전부 — 「제안 없음」 도 설계서로 남고 이력에 쌓였다.
     */
    const reportTabs = useMemo(
        () => rows.filter(x => (design ? !!reports[x.key] : hasFindings(reports[x.key]))), [rows, reports, design],
    );
    const emptyTables = useMemo(
        () => (design ? [] : rows.filter(x => reports[x.key] && !hasFindings(reports[x.key])).map(x => x.key)),
        [rows, reports, design],
    );
    /** 분량 한도로 밀린 설계 가이드 — 등록해 뒀는데 안 실린 규칙이 있다는 것을 감추지 않는다 */
    const droppedGuides = useMemo(() => {
        const set = new Set();
        Object.values(reports).forEach(rp => ((rp && rp.guide && rp.guide.dropped) || []).forEach(g => set.add(g)));
        return [...set];
    }, [reports]);
    const activeKey = useMemo(
        () => (reportTabs.some(x => x.key === reportTab) ? reportTab : (reportTabs[0] || {}).key || ''),
        [reportTabs, reportTab],
    );
    const activeReport = activeKey ? reports[activeKey] : null;

    /**
     * **토큰을 얼마나 썼는지는 언제나 보인다.** 테이블 수만큼 부르는 구조라 더욱 그렇다 —
     * 세 개를 고른 것과 서른 개를 고른 것은 사용자에게 다른 사실이다.
     */
    const reportUsage = useMemo(() => {
        const list = Object.values(reports).map(r => r && r.usage).filter(Boolean);
        if (!list.length) return null;
        return list.reduce((a, u) => ({
            calls: a.calls + 1,
            input: a.input + (u.inputTokens || 0),
            output: a.output + (u.outputTokens || 0),
            // 비용은 제공자가 줄 때만 있다(Claude 는 주고 Codex 는 토큰만 준다)
            cost: a.cost + (typeof u.costUsd === 'number' ? u.costUsd : 0),
        }), { calls: 0, input: 0, output: 0, cost: 0 });
    }, [reports]);

    const no = design ? 2 : 3;   // 설계 탭은 「1 근거」 다음이 리포트다
    return (
        <>
            <section className="tcf-step">
                <div className="tcf-head">
                    <span className="tcf-no">{no}</span>
                    {design ? t(`assistant:design.title.${design}`) : t('assistant:tableAnalysis.stepDiag')}
                </div>
                {/* 진단 판 — AI 진단 · 규칙 진단(AI 없음). 판마다 결과가 따로 남는다 */}
                {!design ? (
                    <div className="tcf-row ta-methodPick" role="radiogroup">
                        {['ai', 'rule'].map(k => (
                            <label className="tcf-choice" key={k} title={t(`assistant:tableAnalysis.methodTip.${k}`)}>
                                <input type="radio" name="ta-diag-method" checked={method === k} disabled={reporting} onChange={() => setMethod(k)} />
                                {t(`assistant:tableAnalysis.method.${k}`)}
                                {Object.keys(byMethod[k]).length ? <span className="ta-meta">({Object.keys(byMethod[k]).length})</span> : null}
                            </label>
                        ))}
                    </div>
                ) : null}
                <div className="tcf-row">
                    {/* 설계는 절을 서버가 정한다(인덱스 설계도 넓은 범위 조회면 파티션 절이 붙는다) */}
                    {!design && SECTIONS.map(sc => (
                        <label className="tcf-choice" key={sc}>
                            <input
                                type="checkbox" checked={sections.includes(sc)}
                                onChange={() => setSections(prev => (prev.includes(sc)
                                    ? prev.filter(x => x !== sc) : [...prev, sc]))}
                            />
                            {t(`assistant:tableAnalysis.diagSection.${sc}`)}
                        </label>
                    ))}
                    <button
                        type="button" className="tcf-btn tcf-primary"
                        disabled={reporting || (!design && !sections.length)}
                        onClick={makeReport}
                    >
                        {reporting ? <FiLoader size={11} className="tcf-spin" /> : (m === 'rule' ? <FiList size={11} /> : <FiCpu size={11} />)}
                        {reporting ? t('assistant:tableAnalysis.reporting')
                            : (design ? t(`assistant:design.make.${design}`)
                                : t(m === 'rule' ? 'assistant:tableAnalysis.makeRule' : 'assistant:tableAnalysis.makeDiag'))}
                    </button>
                    {/* **AI 를 부르는지를 버튼 옆에 적는다.** 규칙 진단은 토큰을 쓰지 않는다 — 누르기 전에 알아야 한다 */}
                    {m === 'rule' ? (
                        <span className="ta-dbonly" title={t('assistant:tableAnalysis.methodTip.rule')}>
                            <FiList size={10} />
                            {t('assistant:tableAnalysis.ruleTag')}
                        </span>
                    ) : (
                        <span className="ta-ai" title={t('assistant:tableAnalysis.aiTip')}>
                            <FiCpu size={10} />
                            {t('assistant:tableAnalysis.aiTag')}
                        </span>
                    )}
                </div>
                {/* 리포트는 **테이블 단위**다 — 몇 번 부르는지를 먼저 말한다 */}
                <div className="tcf-hint">
                    {t('assistant:tableAnalysis.perTable', { count: rows.length })}
                </div>
                {/* 명세: 절은 선택이고 **최소 하나** */}
                {!design && !sections.length ? <div className="tcf-hint">{t('assistant:tableAnalysis.needSection')}</div> : null}
                {design ? <div className="tcf-hint">{t(`assistant:design.makeHint.${design}`)}</div>
                    : <div className="tcf-hint">{t('assistant:tableAnalysis.diagHint')}</div>}
                {/* 참조할 진단 — 테이블마다. 없으면 설계 직전에 규칙 진단을 만들어 쓴다는 것을 미리 말한다 */}
                {design ? (
                    <div className="ta-priorList">
                        <span className="tcf-label">{t('assistant:design.priorHead')}</span>
                        {rows.map((x) => {
                            const p = priorFor(x.key);
                            return (
                                <div className={p ? 'tcf-hint' : 'tcf-hint ta-priorAuto'} key={x.key}>
                                    {p ? t('assistant:design.priorRow', { table: x.key, kind: diagKind(p, t), at: storedTime(p.createdAt) || '-', count: p.findings })
                                        : t('assistant:design.priorAutoRow', { table: x.key })}
                                </div>
                            );
                        })}
                    </div>
                ) : null}
                {/* 참조할 설계 가이드 — 기본은 지금 연결·스키마에 매핑된 것 */}
                {design ? (
                    <div className="ta-guidePick">
                        <div className="tcf-row">
                            <span className="tcf-label">{t('assistant:design.guides', { count: guideSel.length })}</span>
                            <button type="button" className="tcf-btn" onClick={onOpenGuides}>
                                {t('assistant:design.manageGuides')}
                            </button>
                        </div>
                        {!guideChoices.length ? (
                            <div className="ta-gap">
                                {draftGuideCount ? t('assistant:design.noPublishedGuides', { count: draftGuideCount }) : t('assistant:design.noGuides')}
                            </div>
                        ) : null}
                        {guideChoices.map(g => (
                            <label className="tcf-choice ta-guideItem" key={g.id} title={g.description || g.title}>
                                <input type="checkbox" checked={guideSel.includes(g.id)} onChange={() => toggleGuide(g.id)} />
                                <span className="ta-meta">{t(`assistant:designGuide.kind.${g.kind}`)}</span>
                                {g.title}
                                <span className="ta-meta">{t('assistant:designGuide.ruleCount', { count: g.rules.length })}</span>
                                {mappedTo(g, connectionId, schema) ? <span className="tcf-chipModel">{t('assistant:design.mapped')}</span> : null}
                                {design === 'index' && g.kind === 'partition' ? (
                                    <span className="ta-meta">{t('assistant:design.partitionGuideNote')}</span>
                                ) : null}
                            </label>
                        ))}
                    </div>
                ) : null}
                {droppedGuides.length ? (
                    <div className="gm-dropped">
                        <FiAlertCircle size={12} />
                        {t('assistant:design.guideDropped', { count: droppedGuides.length, titles: droppedGuides.join(', ') })}
                    </div>
                ) : null}
                {reportAt ? (
                    <div className="tcf-hint">
                        {t('assistant:tableAnalysis.reportingAt', {
                            done: reportAt.done + 1, total: reportAt.total, table: reportAt.table,
                        })}
                    </div>
                ) : null}
                <Problem error={reportError} onClose={() => setReportError(null)} t={t} />

                {/* **실패한 테이블을 목록에서 지우지 않는다** — 없는 탭이 "문제 없음"
                    으로 읽히면 안 된다. 한 건이 실패해도 나머지는 그대로 남는다. */}
                {Object.keys(reportFails).map(k => (
                    <div className="gm-dropped" key={k}>
                        <FiAlertCircle size={12} />
                        {t('assistant:tableAnalysis.reportFailed', {
                            table: k, message: reportFails[k].message,
                        })}
                    </div>
                ))}
                {emptyTables.length ? (
                    <div className="tcf-hint">
                        {t('assistant:tableAnalysis.noFindings', { tables: emptyTables.join(', ') })}
                    </div>
                ) : null}

                {reportTabs.length ? (
                    <div className="ta-report">
                        {/* 테이블마다 탭 — 종합 리포트는 어느 테이블의 제안인지 흐려진다 */}
                        <div className="ta-tabs" role="tablist">
                            {reportTabs.map(x => (
                                <button
                                    type="button" key={x.key} role="tab"
                                    aria-selected={x.key === activeKey}
                                    className={`ta-tab${x.key === activeKey ? ' ta-tabOn' : ''}`}
                                    onClick={() => setReportTab(x.key)}
                                >
                                    {x.key}
                                </button>
                            ))}
                        </div>
                        {activeReport ? <ReportBody data={activeReport} t={t} /> : null}
                        {/* 설계서 — 테이블이 바뀌면 새로 그린다(고치던 글이 다른 테이블로 넘어가지 않게) */}
                        {activeReport && design ? (
                            <DesignDocPanel
                                key={activeKey}
                                connectionId={connectionId}
                                owner={activeKey.slice(0, activeKey.indexOf('.'))}
                                table={activeKey.slice(activeKey.indexOf('.') + 1)}
                                latestId={activeReport.designId || ''}
                            />
                        ) : null}
                    </div>
                ) : null}

                {/* 사용량은 **언제나 보인다** — 테이블 수만큼 부르는 구조라 더욱 그렇다 */}
                {reportUsage ? (
                    <div className="tcf-hint ta-usage">
                        {t('assistant:tableAnalysis.usage', {
                            calls: reportUsage.calls,
                            input: compact(reportUsage.input),
                            output: compact(reportUsage.output),
                        })}
                        {reportUsage.cost > 0 ? ` · ${money(reportUsage.cost)}` : ''}
                    </div>
                ) : null}
            </section>

            {/* 사례 등록 — **설계 탭에서만, 리포트가 있는 테이블만**(사용자 지적 2026-09-11). 분석 탭은 진단뿐이라 사례의
                「개선 방안」 을 채울 것이 없다(2026-10-06 — 진단/설계 분리). 적을 것이 없는데 버튼을 세우면 빈 사례가 만들어진다. */}
            {design && reportTabs.length ? (
                <section className="tcf-step">
                    <div className="tcf-head">
                        <span className="tcf-no">{no + 1}</span>{t('assistant:tableAnalysis.stepCase')}
                    </div>
                    <div className="tcf-hint">{t('assistant:tableAnalysis.caseHint')}</div>
                    {!registering ? (
                        <div className="tcf-row">
                            {reportTabs.map(x => (
                                <button
                                    type="button" className="tcf-btn" key={x.key}
                                    onClick={() => startRegister(x)}
                                >
                                    <FiPlus size={11} /> {x.key}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="ta-register">
                            <label className="tcf-field">
                                <span className="tcf-label">{t('assistant:caseForm.f.title')}</span>
                                <input
                                    className="tcf-input" value={registering.title}
                                    onChange={(e) => setRegistering(p => ({ ...p, title: e.target.value }))}
                                />
                            </label>
                            {/* 명세의 셋을 **각각** 보여 준다 — 한 칸에 뭉치면 무엇이 근거였는지 못 되짚는다 */}
                            {['body', 'fix', 'evidence'].map(k => (
                                <label className="tcf-field" key={k}>
                                    <span className="tcf-label">{t(`assistant:tableAnalysis.f.${k}`)}</span>
                                    <textarea
                                        className="tcf-input" rows={k === 'evidence' ? 8 : 5}
                                        value={registering[k] || ''}
                                        onChange={(e) => setRegistering(p => ({ ...p, [k]: e.target.value }))}
                                    />
                                </label>
                            ))}
                            <div className="tcf-label">{t('assistant:caseForm.topics')}</div>
                            <div className="tcf-topicAxis">
                                {topicCatalog.map(tp => (
                                    <button
                                        type="button" key={tp.code}
                                        className={`tcf-topic${(registering.topics || []).includes(tp.code) ? ' tcf-topicOn' : ''}`}
                                        title={(registering.topicWhy || {})[tp.code] || tp.desc}
                                        onClick={() => setRegistering(p => ({
                                            ...p,
                                            topics: (p.topics || []).includes(tp.code)
                                                ? p.topics.filter(x => x !== tp.code)
                                                : [...(p.topics || []), tp.code],
                                        }))}
                                    >
                                        {tp.label}
                                    </button>
                                ))}
                            </div>
                            <div className="tcf-hint">{t('assistant:tableAnalysis.topicWhyHint')}</div>
                            <Problem error={saveError} onClose={() => setSaveError(null)} t={t} />
                            <div className="tcf-actions">
                                <button type="button" className="tcf-btn" onClick={() => setRegistering(null)}>
                                    {t('common:ui.cancel')}
                                </button>
                                <button type="button" className="tcf-btn tcf-primary" disabled={saving} onClick={save}>
                                    {saving ? <FiLoader size={11} className="tcf-spin" /> : <FiSave size={11} />}
                                    {t('assistant:caseForm.save')}
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            ) : null}
        </>
    );
}
ReportStage.propTypes = {
    design: PropTypes.oneOf(['index', 'partition']),
    analysis: PropTypes.shape({ tables: PropTypes.array, note: PropTypes.array }).isRequired,
    connectionId: PropTypes.string.isRequired,
    schema: PropTypes.string,
    provider: PropTypes.string,
    prior: PropTypes.object,
    seed: PropTypes.shape({ ai: PropTypes.object, rule: PropTypes.object }),
    onReports: PropTypes.func,
    onOpenGuides: PropTypes.func,
    guideReloadKey: PropTypes.number,
    active: PropTypes.bool,
};
ReportStage.defaultProps = {
    design: null, schema: '', provider: undefined, prior: null, seed: null, onReports: undefined,
    onOpenGuides: undefined, guideReloadKey: 0, active: false,
};

/** 수집한 근거(테이블 하나) — 테이블 분석 탭의 「기초 분석 결과」. AI 를 부르지 않고 계산한 것만 */
/**
 * 저장된 분석 · 진단 이력 — **검색해서 조회**한다(2026-10-07 사용자 요구).
 *
 * 저장(병합·교체)된 원본이 있는 테이블과, 저장 전 수집분으로 진단만 한 테이블이 함께 보인다. 검색어는 테이블 이름 ·
 * SQL 전문/조건 컬럼 · 진단 본문에서 찾고, 어디서 걸렸는지를 꼬리표로 단다. 진단은 테이블마다 이력으로 남아(판마다 최근 20건)
 * 하나를 고르면 그 자리에서 본문을 연다 — 그 뒤 같은 테이블을 다시 저장했으면 「옛 데이터의 진단」 이라고 말한다.
 * 불러오기는 원본이 있는 테이블만(진단만 있는 것은 불러올 사실이 없다).
 */
function SavedBrowser({ connectionId, reloadKey, picked, onTogglePick, onLoad, onRemove, t }) {
    const [q, setQ] = useState('');
    const [applied, setApplied] = useState('');
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openKey, setOpenKey] = useState('');
    const [history, setHistory] = useState([]);
    const [view, setView] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!connectionId) return undefined;
        let alive = true;
        setLoading(true);
        listSavedTables(connectionId, applied)
            .then((r) => { if (alive) setItems(r.saved || []); })
            .catch((err) => { if (alive) setError({ message: err.message }); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [connectionId, applied, reloadKey]);

    const openHistory = useCallback(async (it) => {
        setView(null);
        if (openKey === it.key) { setOpenKey(''); return; }
        setOpenKey(it.key);
        setHistory([]);
        try {
            const r = await listTableDiagnoses({ connectionId, owner: it.owner, table: it.table });
            setHistory(r.diagnoses || []);
        } catch (err) { setError({ message: err.message }); }
    }, [connectionId, openKey]);

    const openDiag = useCallback(async (it, h) => {
        try {
            const r = await getTableDiagnosis(h.id);
            const d = r.diagnosis;
            setView({
                key: it.key, id: h.id,
                data: {
                    ...d, diagnosis: true,
                    // 진단 뒤에 그 테이블을 다시 저장했으면 지금 데이터와 다를 수 있다
                    stale: !!(d.rawSavedAt && it.savedAt && d.rawSavedAt !== it.savedAt),
                },
            });
        } catch (err) { setError({ message: err.message }); }
    }, []);

    return (
        <div className="ta-savedList">
            <div className="tcf-row ta-savedSearch">
                <input
                    className="tcf-input" value={q} placeholder={t('assistant:tableAnalysis.savedSearch')}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setApplied(q.trim()); }}
                />
                <button type="button" className="tcf-btn" onClick={() => setApplied(q.trim())}>
                    {loading ? <FiLoader size={11} className="tcf-spin" /> : <FiSearch size={11} />}
                    {t('assistant:tableAnalysis.find')}
                </button>
                {applied ? (
                    <button type="button" className="tcf-btn" onClick={() => { setQ(''); setApplied(''); }}>
                        {t('assistant:tableAnalysis.savedSearchClear')}
                    </button>
                ) : null}
            </div>
            <div className="tcf-hint">{t('assistant:tableAnalysis.savedSearchHint')}</div>
            <Problem error={error} onClose={() => setError(null)} t={t} />
            {items && !items.length ? (
                <div className="tcf-hint">{t(applied ? 'assistant:tableAnalysis.savedNoMatch' : 'assistant:tableAnalysis.savedNone')}</div>
            ) : null}
            {(items || []).map(it => (
                <div key={it.key} className="ta-savedItem">
                    <div className="ta-savedRow">
                        <label className="tcf-choice">
                            <input
                                type="checkbox" checked={picked.includes(it.key)} disabled={!it.rawSaved}
                                onChange={() => onTogglePick(it.key)}
                            />
                            <b>{it.key}</b>
                        </label>
                        {it.rawSaved ? (
                            <>
                                {it.savedMode ? <span className="tcf-chipModel">{t(`assistant:tableAnalysis.savedModeTag.${it.savedMode}`)}</span> : null}
                                <span className="ta-meta">{t('assistant:tableAnalysis.savedMeta', { at: storedTime(it.savedAt), count: it.sqlCount })}</span>
                            </>
                        ) : <span className="ta-meta">{t('assistant:tableAnalysis.savedDiagOnly')}</span>}
                        {['ai', 'rule'].map(mt => (it.diag && it.diag[mt] ? (
                            <span key={mt} className={mt === 'rule' ? 'ta-dbonly' : 'ta-ai'} title={it.diag[mt].summary}>
                                {t('assistant:tableAnalysis.diagChip', {
                                    kind: diagKind(it.diag[mt], t), at: shortAt(it.diag[mt].createdAt), count: it.diag[mt].findings,
                                })}
                            </span>
                        ) : null))}
                        {(it.matched || []).map(w => (
                            <span key={w} className="ta-matchTag">{t(`assistant:tableAnalysis.matchedIn.${w}`)}</span>
                        ))}
                        <span className="ta-savedActions">
                            <button
                                type="button" className={`tcf-btn${openKey === it.key ? ' ta-histOn' : ''}`}
                                disabled={!it.diag} onClick={() => openHistory(it)}
                            >
                                <FiClock size={11} /> {t('assistant:tableAnalysis.diagHistory')}
                            </button>
                            <button type="button" className="tcf-iconBtn" title={t('common:ui.delete')} onClick={() => onRemove(it)}>
                                <FiX size={11} />
                            </button>
                        </span>
                    </div>
                    {openKey === it.key ? (
                        <div className="ta-histList">
                            {history.map(h => (
                                <button
                                    type="button" key={h.id}
                                    className={`ta-histItem${view && view.id === h.id ? ' ta-histOn' : ''}`}
                                    onClick={() => openDiag(it, h)}
                                >
                                    <span className={h.method === 'rule' ? 'ta-dbonly' : 'ta-ai'}>{diagKind(h, t)}</span>
                                    <span className="ta-meta">{storedTime(h.createdAt)}</span>
                                    <span className="ta-meta">{t('assistant:tableAnalysis.findingsCount', { count: h.findings, high: h.high })}</span>
                                    {h.rawSavedAt && it.savedAt && h.rawSavedAt !== it.savedAt
                                        ? <span className="ta-meta">{t('assistant:tableAnalysis.diagStaleShort')}</span> : null}
                                </button>
                            ))}
                            {view && view.key === it.key ? <ReportBody data={view.data} t={t} /> : null}
                        </div>
                    ) : null}
                </div>
            ))}
            <div className="tcf-row">
                <button type="button" className="tcf-btn tcf-primary" disabled={!picked.length} onClick={onLoad}>
                    {t('assistant:tableAnalysis.savedLoad', { count: picked.length })}
                </button>
                <span className="tcf-hint">{t('assistant:tableAnalysis.savedLoadHint')}</span>
            </div>
        </div>
    );
}
SavedBrowser.propTypes = {
    connectionId: PropTypes.string.isRequired,
    reloadKey: PropTypes.number.isRequired,
    picked: PropTypes.arrayOf(PropTypes.string).isRequired,
    onTogglePick: PropTypes.func.isRequired,
    onLoad: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

function FactsTable({ x, t, onOpenSql, sideOn }) {
    const sqlCount = x.access ? x.access.sqlCount : 0;
    // 「SQL n건」 을 누르면 오른쪽에 원본 쿼리·액세스 패턴(2026-10-06). combo: null = 이 테이블 전부
    const openSql = (combo) => onOpenSql({ owner: x.owner, table: x.table, combo });
    return (
        <div className={`ta-table${sideOn ? ' ta-tableOn' : ''}`}>
            <div className="ta-tableHead">
                {x.key}
                <span className="ta-meta">
                    {t('assistant:tableAnalysis.tableMetaShort', {
                        rows: x.stats.table.numRows === null ? '?' : x.stats.table.numRows,
                        idx: x.stats.indexes.length,
                    })}
                </span>
                {sqlCount ? (
                    <button type="button" className="tcf-linkBtn" onClick={() => openSql(null)} title={t('assistant:tableAnalysis.sqlOpenTip')}>
                        {t('assistant:tableAnalysis.sqlObserved', { count: sqlCount })}
                    </button>
                ) : <span className="ta-meta">{t('assistant:tableAnalysis.sqlObserved', { count: 0 })}</span>}
            </div>
            {/* 근거의 빈틈을 **먼저** 말한다 — 모르는 것 위의 설계안은 근거가 없다 */}
            {!x.access ? (
                <div className="ta-gap">{t('assistant:tableAnalysis.noAccess')}</div>
            ) : null}
            {x.stats.findings.noTableStats || x.stats.findings.staleStats ? (
                <div className="ta-gap">
                    {t(x.stats.findings.noTableStats
                        ? 'assistant:tableAnalysis.noStats' : 'assistant:tableAnalysis.staleStats')}
                </div>
            ) : null}
            {/* **조건 조합** — SQL 하나에서 함께 걸린 조건과 그 SQL 수(2026-10-06 사용자 요구). 결합 인덱스는
                「CUST_ID 14건 · PROD_ID 10건」 이 아니라 「CUST_ID = AND PROD_ID = 5건」 을 근거로 한다 */}
            {x.access && (x.access.combos || []).length ? (
                <ul className="ta-cols ta-combos">
                    {x.access.combos.slice(0, COMBO_SHOWN).map(c => (
                        <li key={c.label || '-'}>
                            {c.terms.length ? c.terms.map((tm, ti) => (
                                <React.Fragment key={`${tm.column}${tm.op}${tm.join}${tm.fn}`}>
                                    {ti ? <span className="ta-comboAnd">AND</span> : null}
                                    {/* INTERNAL_FUNCTION 은 Oracle 이 넣은 암시적 형변환 — 내부 표기 대신 뜻으로 보인다 */}
                                    <b>{tm.fn && !SPECIAL_FN[tm.fn] ? `${tm.fn}(${tm.column})` : tm.column}</b> {tm.op}
                                    {SPECIAL_FN[tm.fn] ? <span className="tcf-chipModel">{t(SPECIAL_FN[tm.fn])}</span> : null}
                                    {tm.join ? <span className="ta-meta">{t('assistant:tableAnalysis.joinTerm')}</span> : null}
                                </React.Fragment>
                            )) : <i>{t('assistant:tableAnalysis.noPredicate')}</i>}
                            {' — '}
                            <button type="button" className="tcf-linkBtn" onClick={() => openSql(c.label || '')} title={t('assistant:tableAnalysis.sqlOpenTip')}>
                                {t('assistant:tableAnalysis.sqlCount', { count: c.sqlCount })}
                            </button>
                        </li>
                    ))}
                    {x.access.combos.length + (x.access.combosOmitted || 0) > COMBO_SHOWN ? (
                        <li className="tcf-hint">
                            {t('assistant:tableAnalysis.combosMore', { count: x.access.combos.length + (x.access.combosOmitted || 0) - COMBO_SHOWN })}
                        </li>
                    ) : null}
                </ul>
            ) : null}
            {/* 컬럼별 주의 — 조합만으로는 안 보이는 것(필터로만 쓰여 범위를 못 줄임, 늘 가공돼 인덱스를 못 탐) */}
            {x.access && x.access.columns.some(c => c.filterOnly || c.alwaysTransformed) ? (
                <div className="tcf-hint">
                    {t('assistant:tableAnalysis.columnCautions')}{' '}
                    {x.access.columns.filter(c => c.filterOnly || c.alwaysTransformed).map(c => (
                        <span key={c.column} className="ta-cautionCol">
                            <b>{c.column}</b>
                            {c.filterOnly ? <span className="tcf-chipModel">{t('assistant:tableAnalysis.filterOnly')}</span> : null}
                            {c.alwaysTransformed ? <span className="tcf-chipModel">{t('assistant:tableAnalysis.transformed')}</span> : null}
                        </span>
                    ))}
                </div>
            ) : null}
            {(x.dist || []).map(d => (
                <div className="tcf-hint" key={`dist-${d.column}`}>
                    {d.months
                        ? t('assistant:design.distLine', {
                            column: d.column, first: d.first, last: d.last,
                            months: d.months, avg: d.avgPerMonth === null ? '?' : d.avgPerMonth,
                        })
                        : t('assistant:design.distEmpty', { column: d.column })}
                    {d.estimated ? ` · ${t('assistant:design.sampled', { pct: d.sampledPct })}` : ''}
                </div>
            ))}
            {x.stats.findings.redundantIndexes.map(r => (
                <div className="ta-gap" key={r.index}>
                    {t('assistant:tableAnalysis.redundant', { a: r.index, b: r.coveredBy })}
                </div>
            ))}
        </div>
    );
}
FactsTable.propTypes = {
    x: PropTypes.object.isRequired, t: PropTypes.func.isRequired,
    onOpenSql: PropTypes.func.isRequired, sideOn: PropTypes.bool,
};
FactsTable.defaultProps = { sideOn: false };

function TableAnalysisModal({ open, mode, connectionId: initialConnectionId, preset, provider, onClose }) {
    const { t } = useTranslation();
    /**
     * 창은 둘이다(2026-10-15 개편) — 메뉴 「인덱스 설계」·「파티션 설계」 가 각각 [테이블 액세스 분석, 그 설계] 두 탭으로 연다.
     * 같은 창 하나가 모드만 바꿔 열리므로 수집한 분석(그리고 불러온 저장 분석)은 두 설계가 함께 쓴다 — 다른 메뉴로 열어도 다시 수집하지 않는다.
     * 옛 'analysis' 모드는 인덱스 설계로 연다.
     */
    const designMode = mode === 'partition' ? 'partition' : 'index';
    const tabs = ['analysis', designMode];
    const [tab, setTab] = useState('analysis');
    // 열 때 — 분석이 있으면 바로 설계 탭, 없으면 분석부터(설계는 분석 결과가 있어야 한다)
    const hasAnalysisRef = useRef(false);
    useEffect(() => { if (open) setTab(hasAnalysisRef.current ? designMode : 'analysis'); }, [open, designMode]);
    /**
     * **연결을 화면에서 고른다.** 편집기 탭의 연결을 그대로 쓰면, 연결 없는 탭에 있을 때
     * 이 화면이 통째로 막힌다 — 분석하려는 DB 와 지금 편집 중인 탭이 같아야 할 이유는 없다.
     * 목록은 탐색기가 이미 들고 있는 것(redux)을 그대로 본다 — 같은 목록을 두 곳에서 읽어 오면
     * 화면마다 다른 연결이 보이는 날이 온다.
     */
    const connections = useSelector(st => (st.schema && st.schema.connections) || []);
    const sessions = useSelector(st => (st.sessions && st.sessions.list) || []);
    const [connectionId, setConnectionId] = useState(initialConnectionId || '');
    // 편집기에서 연결을 바꾸고 이 화면을 다시 열면 그것을 기본값으로 따라간다
    useEffect(() => {
        if (open && initialConnectionId) setConnectionId(prev => prev || initialConnectionId);
    }, [open, initialConnectionId]);
    const conn = useMemo(() => connections.find(c => c.id === connectionId) || null, [connections, connectionId]);
    const connSessions = useMemo(
        () => sessions.filter(x => x.connectionId === connectionId), [sessions, connectionId],
    );
    // 대상은 **고르는 것**이지 적는 것이 아니다 — 오타는 "없는 테이블" 과 구분되지 않는다
    const [schemas, setSchemas] = useState([]);
    const [schema, setSchema] = useState('');
    const [tableQuery, setTableQuery] = useState('');
    const [tableList, setTableList] = useState(null);   // null = 아직 안 읽음
    const [truncated, setTruncated] = useState(false);
    const [picked, setPicked] = useState([]);
    const [listing, setListing] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState(null);      // { tables, collected, note, withDist }
    /** 수집할 때마다 올린다 — 탭마다의 리포트 단계를 새 수집 결과로 갈아 끼운다(앞 수집의 리포트가 새 표에 붙지 않게) */
    const [analysisSeq, setAnalysisSeq] = useState(0);
    // 다른 설계 메뉴로 다시 열 때 볼 값 — 열기 효과가 분석을 의존성에 넣으면 수집이 끝날 때마다 탭이 넘어간다
    hasAnalysisRef.current = !!(analysis && analysis.tables && analysis.tables.length);
    const [analyzeError, setAnalyzeError] = useState(null);
    /** 기간별 행 수도 셀지 — 파티션 범위의 근거지만 테이블 본체를 읽는다(큰 테이블은 표본). 그래서 끌 수 있다 */
    const [withDist, setWithDist] = useState(true);
    /** 테이블 분석 탭의 리포트(진단) — 설계 탭이 함께 쓴다 */
    const [analysisReports, setAnalysisReports] = useState({});
    const [guideModal, setGuideModal] = useState(false);
    const [guideReloadKey, setGuideReloadKey] = useState(0);
    // 원본 저장 — 이미 저장된 테이블의 교체/병합 선택 · 저장 중 · 내가 저장한 목록 · 불러오기 고르기
    const confirm = useConfirm();
    const [saveChoices, setSaveChoices] = useState({});
    const [savingRaw, setSavingRaw] = useState(false);
    const [savedList, setSavedList] = useState([]);
    const [showSaved, setShowSaved] = useState(false);
    const [savedPick, setSavedPick] = useState([]);
    /** 저장 목록을 다시 읽게 하는 키(저장·삭제 뒤) — 검색 상태는 SavedBrowser 가 갖는다 */
    const [savedReload, setSavedReload] = useState(0);
    /** 불러온 분석에 함께 띄울 저장 진단 — { ai: {key: data}, rule: {...} }. 지금 데이터 위의 것만 */
    const [analysisSeed, setAnalysisSeed] = useState(null);
    /** 오른쪽 원본 SQL 패널 — { owner, table, combo } (combo: null = 그 테이블 전부) */
    const [sqlSide, setSqlSide] = useState(null);

    // 연결이 있어야 목록을 읽는다 — 없으면 이 화면은 아무것도 할 수 없다
    useEffect(() => {
        if (!open || !connectionId) return;
        let alive = true;
        setListing(true);
        listAnalysisObjects({ connectionId })
            .then((r) => { if (alive) setSchemas(r.schemas || []); })
            .catch((err) => { if (alive) setAnalyzeError({ message: err.message }); })
            .finally(() => { if (alive) setListing(false); });
        return () => { alive = false; };
    }, [open, connectionId]);

    /** 스키마를 고르면 그 안의 테이블을 읽는다. 검색어로 좁힌다 — 수천 개인 곳이 있다. */
    const loadTables = useCallback(async (sch, q) => {
        if (!sch) { setTableList(null); return; }
        setListing(true);
        try {
            const r = await listAnalysisObjects({ connectionId, schema: sch, q });
            setTableList(r.tables || []);
            setTruncated(!!r.truncated);
        } catch (err) {
            setAnalyzeError({ message: err.message });
        } finally { setListing(false); }
    }, [connectionId]);

    const resetAnalysis = useCallback(() => {
        setSqlSide(null);
        setAnalysisSeed(null);
        setAnalysis(null);
        setAnalysisReports({});
        setAnalysisSeq(n => n + 1);
    }, []);

    /** 연결이 바뀌면 앞 연결의 스키마·테이블·분석은 **전부 뜻이 없다.** */
    const pickConnection = useCallback((id) => {
        setConnectionId(id);
        setSchemas([]);
        setSchema('');
        setPicked([]);
        setTableList(null);
        resetAnalysis();
        setAnalyzeError(null);
    }, [resetAnalysis]);

    const pickSchema = useCallback((sch) => {
        setSchema(sch);
        setPicked([]);          // 스키마가 바뀌면 앞 스키마의 테이블은 뜻이 없다
        setTableQuery('');
        resetAnalysis();
        loadTables(sch, '');
    }, [loadTables, resetAnalysis]);

    /**
     * 채팅(open_table_analysis)이 건넨 표를 고른다 — 「OWNER.NAME」 의 스키마를 고르고, **그 스키마 목록에 실제로 있는 이름만** 고른다
     * (모델이 지어낸 이름이 분석 대상이 되지 않게 — 대상은 고르는 것이라는 이 화면의 규칙과 같다). 스키마를 못 쓰면 아무것도 바꾸지 않는다.
     * 수집은 사람이 누른다. 한 nonce 에 한 번만.
     */
    const presetDoneRef = useRef(null);
    useEffect(() => {
        if (!open || !preset || !connectionId || !schemas.length) return;
        if (presetDoneRef.current === preset.nonce) return;
        presetDoneRef.current = preset.nonce;
        const parsed = (preset.tables || []).map((s) => {
            const parts = String(s).replace(/"/g, '').split('.');
            return parts.length > 1 ? { owner: parts[0].toUpperCase(), name: parts[1].toUpperCase() } : { owner: '', name: parts[0].toUpperCase() };
        }).filter(x => x.name);
        const owners = [...new Set(parsed.map(x => x.owner).filter(Boolean))];
        const known = new Set(schemas.map(sc => String(sc.name).toUpperCase()));
        const sch = owners.length === 1 ? owners[0] : (owners.length === 0 && conn && conn.user ? String(conn.user).toUpperCase() : '');
        if (!sch || !known.has(sch)) return;
        const want = parsed.filter(x => !x.owner || x.owner === sch).map(x => x.name);
        // 한 nonce 에 한 번만 도므로 의존성이 바뀌어도 끊지 않는다(끊으면 다시 돌지 않아 고른 것이 사라진다)
        (async () => {
            setTab('analysis');
            setSchema(sch);
            setTableQuery('');
            resetAnalysis();
            setListing(true);
            try {
                const r = await listAnalysisObjects({ connectionId, schema: sch });
                const list = r.tables || [];
                setTableList(list);
                setTruncated(!!r.truncated);
                setPicked(want.filter(nm => list.some(tb => tb.name === nm)));
            } catch (err) {
                setAnalyzeError({ message: err.message });
            } finally { setListing(false); }
        })();
    }, [open, preset, connectionId, schemas, conn, resetAnalysis]);

    /** 내가 이 연결에 저장한 테이블 */
    const loadSavedList = useCallback(() => {
        if (!connectionId) { setSavedList([]); return; }
        listSavedTables(connectionId)
            .then(r => setSavedList(r.saved || []))
            .catch(() => setSavedList([]));
        setSavedReload(n => n + 1);
    }, [connectionId]);
    useEffect(() => { if (open) loadSavedList(); }, [open, loadSavedList]);

    const run = useCallback(async () => {
        setAnalyzing(true);
        setAnalyzeError(null);
        resetAnalysis();
        try {
            const r = await analyzeTables({ connectionId, schema, tables: picked, dist: withDist });
            setAnalysis({ ...r, withDist, saved: false });
            // 이미 저장된 테이블은 기본 「병합」 — 쌓아 온 SQL 을 말없이 버리지 않는 쪽
            setSaveChoices(Object.fromEntries(Object.keys(r.existing || {}).map(k => [k, 'merge'])));
            setAnalysisSeq(n => n + 1);
            if (r.note && r.note.length) r.note.forEach(n => toast.warning(n));
        } catch (err) {
            setAnalyzeError({ message: err.message });
        } finally { setAnalyzing(false); }
    }, [connectionId, schema, picked, withDist, resetAnalysis]);

    /**
     * 저장 — 테이블별 교체/병합(이미 저장된 것만 고른다). 저장 뒤 화면은 **저장된 원본에서 다시 만든 요약**으로 바뀐다
     * (병합이면 앞서 쌓은 SQL 까지). 데이터가 바뀌었을 수 있으므로 탭마다의 리포트는 새로 시작한다.
     */
    const saveRaw = useCallback(async () => {
        if (!analysis || !analysis.collectId) return;
        setSavingRaw(true);
        try {
            const r = await saveTableAnalysis({ connectionId, collectId: analysis.collectId, choices: saveChoices });
            setAnalysis(prev => ({ ...prev, tables: r.tables, saved: true, collectId: null, existing: {} }));
            setSqlSide(null);
            setAnalysisReports({});
            setAnalysisSeq(n => n + 1);
            toast.success(t('assistant:tableAnalysis.rawSavedToast', { count: r.saved.length }));
            loadSavedList();
        } catch (err) {
            setAnalyzeError({ message: err.message });
        } finally { setSavingRaw(false); }
    }, [analysis, connectionId, saveChoices, loadSavedList, t]);

    /** 저장된 분석 불러오기 — 수집 없이(원본에서 다시 만든 요약) */
    const loadSaved = useCallback(async () => {
        try {
            const r = await loadSavedTables({ connectionId, tables: savedPick });
            // 그 테이블들의 저장 진단 — **지금 데이터(저장 시각) 위에서 한 것만** 함께 띄운다. 옛 데이터의 진단은 이력에서 본다
            const lt = await latestTableDiagnoses({ connectionId, tables: r.tables.map(x => x.key) }).catch(() => ({ latest: {} }));
            const seed = { ai: {}, rule: {} };
            let seeded = 0;
            for (const x of r.tables) {
                for (const mt of ['ai', 'rule']) {
                    const d = ((lt.latest || {})[x.key] || {})[mt];
                    if (d && d.report && d.rawSavedAt && d.rawSavedAt === x.savedAt) {
                        seed[mt][x.key] = { ...d, diagnosis: true, diagId: d.id };
                        seeded += 1;
                    }
                }
            }
            resetAnalysis();
            setAnalysisSeed(seeded ? seed : null);
            setAnalysis({
                tables: r.tables, collected: null, note: [], saved: true, loaded: true, seeded,
                withDist: r.tables.some(x => (x.dist || []).length),
            });
            setAnalysisSeq(n => n + 1);
            setShowSaved(false);
            // 가이드 매핑의 기본 선택이 스키마를 보므로, 한 스키마에서만 불러왔으면 그것으로 맞춘다
            const owners = [...new Set(r.tables.map(x => x.owner))];
            if (owners.length === 1) setSchema(owners[0]);
        } catch (err) {
            setAnalyzeError({ message: err.message });
        }
    }, [connectionId, savedPick, resetAnalysis]);

    const removeSaved = useCallback(async (item) => {
        const ok = await confirm({
            title: t('assistant:tableAnalysis.savedDeleteTitle'),
            message: t('assistant:tableAnalysis.savedDeleteConfirm', { table: item.key }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        try {
            await deleteSavedTable({ connectionId, owner: item.owner, table: item.table });
            setSavedPick(p => p.filter(k => k !== item.key));
            loadSavedList();
        } catch (err) {
            setAnalyzeError({ message: err.message });
        }
    }, [connectionId, confirm, loadSavedList, t]);

    const rows = useMemo(() => (analysis ? analysis.tables : []), [analysis]);
    const openGuides = useCallback(() => setGuideModal(true), []);
    // 창처럼 — 끌어 이동 · 최대화 · 새 창으로 분리(다른 모니터에서 전체 화면). common/useModalWindow (2026-10-06)
    const win = useModalWindow({ title: t(TAB_LABEL[designMode]) });
    const { reset: resetWin } = win;
    useEffect(() => { if (!open) resetWin(); }, [open, resetWin]);
    const existingKeys = useMemo(() => Object.keys((analysis && analysis.existing) || {}), [analysis]);

    if (!open) return null;
    const stageProps = {
        analysis: analysis || { tables: [], note: [] }, connectionId, schema, provider,
        onOpenGuides: openGuides, guideReloadKey,
    };
    return (
        <>
        {win.wrap(
        <div className="tcase-overlay" role="presentation" style={win.overlayStyle}>
            <div className="app-modal tcase-modal" data-app-modal role="presentation" ref={win.frameRef} style={win.frameStyle}>
                <div className="app-modal-header tcase-header" {...win.headerProps}>
                    <span className="app-modal-title tcase-title">
                        <FiDatabase size={14} /> {t(TAB_LABEL[designMode])}
                    </span>
                    <span className="app-modal-header-right">
                    {win.controls}
                    <button type="button" className="app-modal-close tcase-close" onClick={onClose} title={t('common:ui.close')}>
                        <FiX size={16} />
                    </button>
                    </span>
                </div>
                {/* 리본 — 테이블 액세스 분석(수집·진단) → 이 창의 설계(분석 결과를 쓴다). 대상·수집은 두 창이 함께 쓴다 */}
                <div className="ta-ribbon" role="tablist">
                    {tabs.map(k => (
                        <button
                            type="button" key={k} role="tab" aria-selected={tab === k}
                            className={`ta-ribbonTab${tab === k ? ' ta-ribbonOn' : ''}`}
                            onClick={() => setTab(k)}
                        >
                            {k === 'analysis' ? <FiSearch size={12} /> : <FiLayers size={12} />}
                            {t(TAB_LABEL[k])}
                            {k !== 'analysis' && !analysis ? <span className="ta-ribbonNeed" title={t('assistant:design.needAnalysisShort')}>·</span> : null}
                        </button>
                    ))}
                    {analysis ? (
                        <span className="ta-ribbonInfo">
                            {t('assistant:design.basis', { count: rows.length, tables: rows.slice(0, 3).map(x => x.key).join(', ') + (rows.length > 3 ? '…' : '') })}
                        </span>
                    ) : null}
                </div>

                <div className={`ta-body${sqlSide && tab === 'analysis' ? ' ta-bodySplit' : ''}`}>
                <div className="tcase-formWrap">
                    {/*
                      **연결이 없으면 이 화면은 아무것도 할 수 없다.** 대화는 연결 없이도 되지만
                      테이블 분석은 DB 의 통계와 실행계획을 읽는 일이라 연결이 전제다. 단계를
                      그려 놓고 버튼만 비활성으로 두면 "왜 안 되지" 를 사용자가 추측하게 된다.
                    */}
                    {!connections.length ? (
                        <div className="tcf"><section className="tcf-step">
                            <div className="ta-gap">{t('assistant:tableAnalysis.needConnection')}</div>
                        </section></div>
                    ) : (
                    <>
                    {/* ── 테이블 분석 탭 ── */}
                    <div className="tcf ta-tabPane" hidden={tab !== 'analysis'}>
                        {/* 1 대상 */}
                        <section className="tcf-step ta-target">
                            <div className="tcf-head">
                                <span className="tcf-no">1</span>{t('assistant:tableAnalysis.stepTarget')}
                            </div>
                            {/* 연결·스키마·검색을 **한 줄에** — 왼쪽에서 오른쪽이 고르는 순서다 */}
                            <div className="tcf-row">
                                <select
                                    className="tcf-input ta-narrow" value={connectionId}
                                    onChange={(e) => pickConnection(e.target.value)}
                                >
                                    <option value="">{t('assistant:tableAnalysis.pickConnection')}</option>
                                    {connections.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name || c.id}{c.dbType ? ` · ${c.dbType}` : ''}
                                        </option>
                                    ))}
                                </select>
                                {/* 스키마는 **고른다** */}
                                <select
                                    className="tcf-input ta-narrow" value={schema}
                                    disabled={!connectionId || listing}
                                    onChange={(e) => pickSchema(e.target.value)}
                                >
                                    <option value="">{t('assistant:tableAnalysis.pickSchema')}</option>
                                    {schemas.map(sc => (
                                        <option key={sc.name} value={sc.name}>
                                            {sc.name} ({sc.tables})
                                        </option>
                                    ))}
                                </select>
                                <input
                                    className="tcf-input" value={tableQuery} disabled={!schema}
                                    placeholder={t('assistant:tableAnalysis.tableFilter')}
                                    onChange={(e) => { setTableQuery(e.target.value); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') loadTables(schema, tableQuery); }}
                                />
                                <button
                                    type="button" className="tcf-btn" disabled={!schema || listing}
                                    onClick={() => loadTables(schema, tableQuery)}
                                >
                                    {listing ? <FiLoader size={11} className="tcf-spin" /> : <FiSearch size={11} />}
                                    {t('assistant:tableAnalysis.find')}
                                </button>
                            </div>
                            {/* **세션을 뺏지 않는다.** 분석은 카탈로그를 길게 읽는 일이라, 편집기
                                세션을 빌려 쓰면 그 세션이 그동안 묶인다. 서버가 읽기 전용 세션을
                                따로 열고 끝나면 닫는다 — 그 사실을 감추지 않는다. */}
                            {conn ? (
                                <div className="tcf-hint">
                                    {t('assistant:tableAnalysis.sessionInfo', { count: connSessions.length })}
                                </div>
                            ) : null}
                            {conn && conn.dbType && conn.dbType !== 'oracle' ? (
                                <div className="ta-gap">{t('assistant:tableAnalysis.oracleOnly', { db: conn.dbType })}</div>
                            ) : null}

                            {/* 고른 것 — 몇 개를 분석하는지가 늘 보여야 한다.
                                전부 고르면 수십 개가 되므로 앞의 몇 개만 적고 나머지는 수로 말한다. */}
                            {picked.length ? (
                                <div className="tcf-sources ta-picked">
                                    {picked.slice(0, CHIP_MAX).map(nm => (
                                        <span key={nm} className="tcf-chip">
                                            {nm}
                                            <button
                                                type="button" title={t('common:ui.delete')}
                                                onClick={() => setPicked(prev => prev.filter(x => x !== nm))}
                                            >
                                                <FiX size={10} />
                                            </button>
                                        </span>
                                    ))}
                                    {picked.length > CHIP_MAX ? (
                                        <span className="tcf-hint">
                                            {t('assistant:tableAnalysis.chipMore', { count: picked.length - CHIP_MAX })}
                                        </span>
                                    ) : null}
                                </div>
                            ) : null}

                            {tableList && tableList.length ? (
                                /* **스키마의 테이블을 통째로 고를 수 있어야 한다.** 리포트가 테이블
                                   단위라 하나씩 누르면 수십 번이다. 고르는 것은 지금 **목록에 있는
                                   것**뿐이라(50개 상한) 잘렸으면 그 사실이 아래에 그대로 남는다. */
                                <div className="tcf-row ta-pickBar">
                                    <button
                                        type="button" className="tcf-btn"
                                        onClick={() => setPicked(tableList.map(tb => tb.name))}
                                    >
                                        {t('assistant:tableAnalysis.pickAll', { count: tableList.length })}
                                    </button>
                                    <button
                                        type="button" className="tcf-btn" disabled={!picked.length}
                                        onClick={() => setPicked([])}
                                    >
                                        {t('assistant:tableAnalysis.pickNone')}
                                    </button>
                                    <span className="tcf-hint">
                                        {t('assistant:tableAnalysis.pickedCount', { count: picked.length })}
                                    </span>
                                </div>
                            ) : null}

                            {tableList ? (
                                <div className="ta-picker">
                                    {tableList.length === 0 ? (
                                        <div className="tcf-hint">{t('assistant:tableAnalysis.noTables')}</div>
                                    ) : null}
                                    {tableList.map(tb => (
                                        <button
                                            type="button" key={tb.name}
                                            className={`ta-pick${picked.includes(tb.name) ? ' ta-pickOn' : ''}`}
                                            title={tb.comment || tb.name}
                                            onClick={() => setPicked(prev => (prev.includes(tb.name)
                                                ? prev.filter(x => x !== tb.name) : [...prev, tb.name]))}
                                        >
                                            <span className="ta-pickName">{tb.name}</span>
                                            {tb.type !== 'TABLE' ? <span className="ta-meta">{tb.type}</span> : null}
                                            {tb.comment ? <span className="ta-meta">{tb.comment}</span> : null}
                                        </button>
                                    ))}
                                    {/* **잘렸다는 사실을 감추지 않는다** — 50개만 보고 전부라고 믿으면 대상을 빠뜨린다 */}
                                    {truncated ? <div className="ta-gap">{t('assistant:tableAnalysis.truncated')}</div> : null}
                                </div>
                            ) : null}

                            <div className="tcf-row">
                                <button
                                    type="button" className="tcf-btn tcf-primary"
                                    disabled={analyzing || !schema}
                                    onClick={run}
                                >
                                    {analyzing ? <FiLoader size={11} className="tcf-spin" /> : <FiSearch size={11} />}
                                    {analyzing ? t('assistant:tableAnalysis.analyzing')
                                        : t(picked.length ? 'assistant:tableAnalysis.analyzePicked' : 'assistant:tableAnalysis.analyzeAll',
                                            { count: picked.length })}
                                </button>
                                {/* **이 버튼은 AI 를 부르지 않는다.** 딕셔너리와 실행계획을 읽을 뿐이다 */}
                                <span className="ta-dbonly" title={t('assistant:tableAnalysis.dbOnlyTip')}>
                                    <FiDatabase size={10} />
                                    {t('assistant:tableAnalysis.dbOnlyTag')}
                                </span>
                                {/* 기간별 행 수 — 테이블 본체를 읽으므로 끌 수 있게 둔다(파티션 설계의 범위 근거) */}
                                <label className="tcf-choice" title={t('assistant:design.distHint')}>
                                    <input type="checkbox" checked={withDist} onChange={(e) => setWithDist(e.target.checked)} />
                                    {t('assistant:tableAnalysis.withDist')}
                                </label>
                            </div>
                            <div className="tcf-hint">{t('assistant:tableAnalysis.targetHint')}</div>
                            {withDist ? <div className="tcf-hint">{t('assistant:design.distHint')}</div> : null}
                            {/* 저장된 분석 — 수집 없이 불러와 설계에 쓴다(내 것만) */}
                            {connectionId ? (
                                <div className="tcf-row">
                                    <button type="button" className={`tcf-btn${showSaved ? ' ta-histOn' : ''}`} onClick={() => setShowSaved(v => !v)}>
                                        <FiClock size={11} /> {t('assistant:tableAnalysis.savedOpen', { count: savedList.length })}
                                    </button>
                                </div>
                            ) : null}
                            {showSaved && connectionId ? (
                                <SavedBrowser
                                    connectionId={connectionId} reloadKey={savedReload} t={t}
                                    picked={savedPick}
                                    onTogglePick={(k) => setSavedPick(p => (p.includes(k) ? p.filter(x => x !== k) : [...p, k]))}
                                    onLoad={loadSaved} onRemove={removeSaved}
                                />
                            ) : null}
                            <Problem error={analyzeError} onClose={() => setAnalyzeError(null)} t={t} />
                        </section>

                        {/* 2 근거·기초 분석 — **AI 를 부르기 전에** 무엇을 읽었는지 보여 준다 */}
                        {analysis ? (
                            <section className="tcf-step">
                                <div className="tcf-head">
                                    <span className="tcf-no">2</span>{t('assistant:tableAnalysis.stepFacts')}
                                </div>
                                <div className="tcf-hint">
                                    {analysis.loaded ? (analysis.seeded ? t('assistant:tableAnalysis.loadedWithDiag', { count: analysis.seeded }) : t('assistant:tableAnalysis.loadedFrom'))
                                        : t('assistant:tableAnalysis.collectedNow', {
                                            plans: (analysis.collected && analysis.collected.plans) || 0,
                                            sqls: (analysis.collected && analysis.collected.sqls) || 0,
                                        })}
                                </div>
                                {/* 원본 저장 — 수집은 저장하지 않는다. 이미 저장된 테이블은 교체/병합을 테이블별로 고른다 */}
                                {analysis.saved ? (
                                    <div className="ta-saveBar"><span className="ta-savedTag">{t('assistant:tableAnalysis.rawIsSaved')}</span></div>
                                ) : (
                                    <div className="ta-saveBar">
                                        <span className="ta-unsavedTag">{t('assistant:tableAnalysis.rawNotSaved')}</span>
                                        {existingKeys.length ? (
                                            <div className="ta-saveChoices">
                                                <div className="tcf-hint">{t('assistant:tableAnalysis.saveChoiceHint')}</div>
                                                {existingKeys.map(k => (
                                                    <div className="ta-savedRow" key={k}>
                                                        <b>{k}</b>
                                                        <span className="ta-meta">
                                                            {t('assistant:tableAnalysis.savedMeta', { at: storedTime(analysis.existing[k].savedAt), count: analysis.existing[k].sqlCount })}
                                                        </span>
                                                        {['merge', 'replace'].map(m => (
                                                            <label className="tcf-choice" key={m} title={t(`assistant:tableAnalysis.saveMode.${m}Tip`)}>
                                                                <input
                                                                    type="radio" name={`save-${k}`} checked={saveChoices[k] === m}
                                                                    onChange={() => setSaveChoices(c => ({ ...c, [k]: m }))}
                                                                />
                                                                {t(`assistant:tableAnalysis.saveMode.${m}`)}
                                                            </label>
                                                        ))}
                                                    </div>
                                                ))}
                                                {existingKeys.length > 1 ? (
                                                    <div className="tcf-row">
                                                        {['merge', 'replace'].map(m => (
                                                            <button
                                                                type="button" className="tcf-btn" key={m}
                                                                onClick={() => setSaveChoices(Object.fromEntries(existingKeys.map(k => [k, m])))}
                                                            >
                                                                {t(`assistant:tableAnalysis.saveAll.${m}`)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        <button type="button" className="tcf-btn tcf-primary" disabled={savingRaw || !analysis.collectId} onClick={saveRaw}>
                                            {savingRaw ? <FiLoader size={11} className="tcf-spin" /> : <FiSave size={11} />}
                                            {t('assistant:tableAnalysis.rawSave')}
                                        </button>
                                        <span className="tcf-hint">{t('assistant:tableAnalysis.rawSaveHint')}</span>
                                    </div>
                                )}
                                {analysis.note.map(n => (
                                    <div className="gm-dropped" key={n}><FiAlertCircle size={12} />{n}</div>
                                ))}
                                {rows.map(x => (
                                    <FactsTable
                                        key={x.key} x={x} t={t} onOpenSql={setSqlSide}
                                        sideOn={!!sqlSide && sqlSide.owner === x.owner && sqlSide.table === x.table}
                                    />
                                ))}
                                {/* 다음 걸음 — 이 결과로 설계한다 */}
                                <div className="tcf-row ta-nextStep">
                                    <button type="button" className="tcf-btn" onClick={() => setTab('index')}>
                                        <FiLayers size={11} /> {t('assistant:design.toIndex')}
                                    </button>
                                    <button type="button" className="tcf-btn" onClick={() => setTab('partition')}>
                                        <FiLayers size={11} /> {t('assistant:design.toPartition')}
                                    </button>
                                </div>
                            </section>
                        ) : null}

                        {/* 3 분석 리포트(진단) — 설계 탭이 이 진단을 함께 받는다 */}
                        {analysis && rows.length ? (
                            <ReportStage
                                key={`analysis-${analysisSeq}`} {...stageProps} seed={analysisSeed}
                                active={tab === 'analysis'} onReports={setAnalysisReports}
                            />
                        ) : null}
                    </div>

                    {/* ── 인덱스 설계 · 파티션 설계 탭 — 테이블 분석의 결과를 쓴다 ── */}
                    {['index', 'partition'].map(d => (
                        <div className="tcf ta-tabPane" key={d} hidden={tab !== d}>
                            {!analysis || !rows.length ? (
                                <section className="tcf-step">
                                    <div className="ta-gap">{t('assistant:design.needAnalysis')}</div>
                                    <div className="tcf-row">
                                        <button type="button" className="tcf-btn tcf-primary" onClick={() => setTab('analysis')}>
                                            <FiSearch size={11} /> {t('assistant:design.goAnalysis')}
                                        </button>
                                    </div>
                                </section>
                            ) : (
                                <>
                                    <section className="tcf-step">
                                        <div className="tcf-head">
                                            <span className="tcf-no">1</span>{t('assistant:design.basisHead')}
                                        </div>
                                        <div className="tcf-hint">
                                            {t('assistant:design.basis', { count: rows.length, tables: rows.map(x => x.key).join(', ') })}
                                            {' '}
                                            <button type="button" className="tcf-linkBtn" onClick={() => setTab('analysis')}>
                                                {t('assistant:design.seeAnalysis')}
                                            </button>
                                        </div>
                                        {/* 파티션 범위의 근거가 없으면 미리 말한다 — 수집 때 기간별 행 수를 껐다 */}
                                        {!analysis.withDist ? <div className="ta-gap">{t('assistant:design.noDistWarn')}</div> : null}
                                    </section>
                                    <ReportStage
                                        key={`${d}-${analysisSeq}`} {...stageProps}
                                        design={d} active={tab === d} prior={analysisReports}
                                    />
                                </>
                            )}
                        </div>
                    ))}
                    </>
                    )}
                </div>
                {/* 원본 쿼리·액세스 패턴 — 테이블 분석 탭에서만. 저장 전이면 수집분(collectId), 저장 뒤면 저장된 원본 */}
                {sqlSide && tab === 'analysis' && analysis ? (
                    <TableRawPanel
                        key={`${sqlSide.owner}.${sqlSide.table}`}
                        connectionId={connectionId} owner={sqlSide.owner} table={sqlSide.table}
                        collectId={analysis.saved ? '' : (analysis.collectId || '')}
                        combo={sqlSide.combo} onClose={() => setSqlSide(null)}
                    />
                ) : null}
                </div>
            </div>
        </div>
        )}
        {/* 설계 가이드 관리 — 이 창 **밖에** 그린다. 닫으면 설계 탭이 목록을 다시 읽는다 */}
        <DesignGuideModal
            open={guideModal} initialKind={tab === 'partition' ? 'partition' : 'index'} provider={provider}
            onClose={() => { setGuideModal(false); setGuideReloadKey(n => n + 1); }}
        />
        </>
    );
}

TableAnalysisModal.propTypes = {
    open: PropTypes.bool,
    /** 'index' | 'partition' — 옛 'analysis' 는 인덱스 설계로 연다 */
    mode: PropTypes.oneOf(['analysis', 'index', 'partition']),
    connectionId: PropTypes.string,
    /** 채팅이 건넨 표 — { tables: ['OWNER.NAME'], nonce } (목록에 있는 것만 고른다) */
    preset: PropTypes.shape({ tables: PropTypes.arrayOf(PropTypes.string), nonce: PropTypes.number }),
    provider: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};
TableAnalysisModal.defaultProps = { open: false, mode: 'index', connectionId: '', preset: null, provider: undefined };

// 작업 탭을 오갈 때 App 이 새 콜백을 넘겨도 창을 통째로 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 탭 전환이 멈칫했다)
export default memoWindow(TableAnalysisModal);
